import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Fetch FE Base list for select dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeBankAccountsMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    account_no: '',
    bank_name: '',
    branch_name: '',
    ifsc_code: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'account_no', field: 'account_no', headerName: 'Account Number', width: 180 },
    { id: 'bank_name', field: 'bank_name', headerName: 'Bank Name', width: 180 },
    { id: 'branch_name', field: 'branch_name', headerName: 'Branch Name', width: 180 },
    { id: 'ifsc_code', field: 'ifsc_code', headerName: 'IFSC Code', width: 150 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchBankAccounts = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/fe-mgmt/fe-bank-accounts/`)
      .then((response) => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            account_no: item.account_no,
            bank_name: item.bank_name,
            branch_name: item.branch_name,
            ifsc_code: item.ifsc_code,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch bank accounts:', error);
        setSnackbarMessage('Failed to load Bank Accounts.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchBankAccounts();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      financial_establishment: '',
      account_no: '',
      bank_name: '',
      branch_name: '',
      ifsc_code: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormData({
      financial_establishment: feBaseObj ? feBaseObj.id : '',
      account_no: item.account_no,
      bank_name: item.bank_name,
      branch_name: item.branch_name,
      ifsc_code: item.ifsc_code,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api
      .delete(`/fe-mgmt/fe-bank-accounts/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Bank Account deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchBankAccounts();
      })
      .catch((error) => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Bank Account.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment) errors.financial_establishment = 'FE Base is required';
    if (!formData.account_no?.trim()) errors.account_no = 'Account number is required';
    if (!formData.bank_name?.trim()) errors.bank_name = 'Bank name is required';
    if (!formData.branch_name?.trim()) errors.branch_name = 'Branch name is required';
    if (!formData.ifsc_code?.trim()) errors.ifsc_code = 'IFSC code is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      financial_establishment: formData.financial_establishment,
      account_no: formData.account_no,
      bank_name: formData.bank_name,
      branch_name: formData.branch_name,
      ifsc_code: formData.ifsc_code,
    };
    try {
      setUploading(true);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-bank-accounts/${selectedItem.id}/`, payload);
        setSnackbarMessage('Bank Account updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-bank-accounts/`, payload);
        setSnackbarMessage('Bank Account added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchBankAccounts();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit Bank Account.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size={30} />;
  }

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          FE Bank Accounts
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)}>
        <DialogTitle>{isEditMode ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="FE Base"
            fullWidth
            value={formData.financial_establishment}
            error={!!formErrors.financial_establishment}
            helperText={formErrors.financial_establishment}
            onChange={(e) => setFormData({ ...formData, financial_establishment: e.target.value })}
            disabled={uploading}
          >
            {feBaseList.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name_of_fe || option.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Account Number"
            fullWidth
            value={formData.account_no}
            error={!!formErrors.account_no}
            helperText={formErrors.account_no}
            onChange={(e) => setFormData({ ...formData, account_no: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Bank Name"
            fullWidth
            value={formData.bank_name}
            error={!!formErrors.bank_name}
            helperText={formErrors.bank_name}
            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Branch Name"
            fullWidth
            value={formData.branch_name}
            error={!!formErrors.branch_name}
            helperText={formErrors.branch_name}
            onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="IFSC Code"
            fullWidth
            value={formData.ifsc_code}
            error={!!formErrors.ifsc_code}
            helperText={formErrors.ifsc_code}
            onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })}
            disabled={uploading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Bank Account?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};
